import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROLE_LABEL_KEY } from '@/auth/roleLabels';
import { useAdminPanelContext } from '@/contexts/AdminPanelContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import { canAccessRecognitionEntry, useUserContext } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { useVisibleNavLinks, type ViewId } from '@/hooks/useVisibleNavLinks';
import { isAdminPath, RECOGNITION_ADMIN_HOME, VIEW_PATHS } from '@/router/pulsoRoutes';
import {
  CAMPAIGN_NAV_ID, EXCLUSIVE_RECOGNITION_NAV_IDS, RECOGNITION_NAV_LINKS, recognitionIconClass,
} from '@/views/recognition/recognitionNavLinks';
import { useCampaignNavEntry, useRecognitionEntryGate } from '@/views/recognition/useCampaignNavEntry';
import { useUserProfile, useAvatarUrl } from '@/api/recognition';
import { getInitials } from '@/utils/strings';
import { avatarColorForSeed, avatarVars } from '@/utils/avatarPalette';
import { LanguageMenu } from '@/components/common/LanguageMenu';
import { LogoutButton } from '@/components/common/LogoutButton';
import { PageSpinner } from '@/components/common';
import { NotificationBell } from '@/views/recognition/components';
import { AdminFlyout } from './Sidebar/AdminFlyout';
import './Sidebar.css';

export type { ViewId };

/**
 * Zona en la que se monta el menú. Es UN SOLO menú para toda la aplicación (T-2), y desde que
 * Pulso y Reconocimiento comparten `BrowserRouter` (Fase 1) todas las entradas navegan igual, con
 * `NavLink`. El pie muestra siempre `LanguageMenu` (Task 13 — antes vivía duplicado en el
 * `GlobalLangDropdown` flotante de la zona `recognition`, ya retirado); lo único que sigue
 * cambiando entre zonas es que `recognition` además agrega la campana de notificaciones.
 */
export type SidebarVariant = 'portal' | 'recognition';

/** Clave por zona: plegar el menú del portal no debe plegar el del módulo, y viceversa. */
const COLLAPSED_KEY: Record<SidebarVariant, string> = {
  portal: 'sigma-portal-sidebar-collapsed',
  recognition: 'sigma-reco-sidebar-collapsed',
};

interface SidebarProps {
  variant?: SidebarVariant;
}

interface SideProfileAvatarProps {
  readonly fotoUrl: string | null | undefined;
  readonly seed: string;
  readonly initials: string;
  readonly ariaLabel: string;
}

/**
 * Círculo del propio usuario, al pie del riel: foto de Graph si hay, si no, iniciales con la
 * paleta corporativa. Se define a nivel de módulo, nunca dentro de `Sidebar` (regla de ESLint
 * `react/no-unstable-nested-components`), y de paso mantiene la complejidad cognitiva de `Sidebar`
 * a raya.
 *
 * Excepción a <InitialsAvatar>: este círculo alterna foto/iniciales y necesita su propio
 * `aria-label` (no hay nombre visible al lado, a diferencia de una fila de lista) — el componente
 * siempre imprime un <span aria-hidden>, así que aquí se aplican directamente las mismas funciones
 * de paleta que usa por debajo.
 */
function SideProfileAvatar({ fotoUrl, seed, initials, ariaLabel }: SideProfileAvatarProps) {
  return (
    <div
      className={`side-avatar${fotoUrl ? '' : ' pmp-avatar'}`}
      style={fotoUrl ? undefined : avatarVars(avatarColorForSeed(seed))}
      aria-label={ariaLabel}
    >
      {fotoUrl
        ? <img src={fotoUrl} alt="" className="avatar-img" width={36} height={36} />
        : initials}
    </div>
  );
}

export function Sidebar({ variant = 'portal' }: SidebarProps) {
  const { t } = useTranslation();
  // El módulo de Reconocimiento aporta sus propios textos: el portal reutiliza los mismos, no los
  // duplica, para que las dos zonas nombren igual cada entrada.
  const { t: tReco } = useTranslation('recognition');
  const { theme, toggle } = useThemeContext();
  const { demoUser } = useUserContext();
  const currentUser = useCurrentUser();
  // Fuente única de las entradas del menú y sus reglas de acceso (rol + permisos granulares)
  const { links: visibleLinks, activeRole, profileResolved } = useVisibleNavLinks();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed(COLLAPSED_KEY[variant]);

  // Entrada "Administración": en vez de navegar directo, abre/cierra un flyout con las secciones
  // del panel (AdminFlyout, Task 10) — el estado de "qué pestaña" vive en AdminPanelContext
  // (Task 8), compartido con AdminView. Cerrar al hacer clic fuera reutiliza el mismo patrón que
  // LanguageMenu (ver src/components/common/LanguageMenu/index.tsx).
  //
  // `isAdminFlyoutOpen` vive en AdminPanelContext (no como useState local) desde el fix de huecos
  // de Task 12: el nav inferior móvil (MobileNav.tsx) necesita abrir/cerrar el mismo flyout desde
  // fuera del árbol de Sidebar, que en `≤768px` queda oculto por completo (`.side-nav { display:
  // none }`, ver src/components/layout/Sidebar.css).
  const { isAdminFlyoutOpen, setIsAdminFlyoutOpen } = useAdminPanelContext();
  const navigate = useNavigate();
  const location = useLocation();
  const adminLinkRef = useRef<HTMLDivElement>(null);
  // Alto máximo del submenú INLINE de Administración (con el riel expandido): sin tope, un rol con
  // muchas secciones (AdminGlobal, 21 ítems) empuja fuera de vista el botón de colapsar, idioma,
  // modo oscuro y el pie de perfil — reportado por QA. Se calcula, no se fija a un valor arbitrario,
  // para adaptarse al alto real de pantalla del usuario y a lo que ocupa el resto del riel (que
  // varía por rol/zona: la campana de notificaciones solo aparece en Reconocimiento).
  const adminButtonRef = useRef<HTMLButtonElement>(null);
  const collapseBtnRef = useRef<HTMLButtonElement>(null);
  const sideNavBottomRef = useRef<HTMLDivElement>(null);
  const [adminFlyoutMaxHeight, setAdminFlyoutMaxHeight] = useState<number | null>(null);
  // BUG QA — "no se muestran correctamente las opciones del menú" con el riel colapsado. El
  // popover crecía SIEMPRE hacia arriba asumiendo que el botón vive cerca del fondo de la
  // pantalla — cierto con el riel lleno, falso con pocos íconos (Reconocer/Perfil/…): ahí el botón
  // queda cerca del TOPE, así que crecer hacia arriba deja apenas ese hueco corto y el resto de
  // secciones solo se veían con scroll (con scrollbar delgada/transparente, parecía simplemente
  // recortado). Se decide la dirección por el lado con MÁS espacio libre, mismo criterio "flip" de
  // cualquier popover/tooltip estándar — ver `admin-link-wrap--flyout-down` en Sidebar.css.
  const [adminFlyoutOpensDown, setAdminFlyoutOpensDown] = useState(false);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const clickTarget = event.target as Node;
      // "Dentro" cubre MÁS que `adminLinkRef`: desde el fix de huecos de Task 12 hay una segunda
      // instancia de `AdminFlyout` montada en `MobileNav.tsx` (nav inferior móvil), fuera de este
      // ref por diseño (ver Sidebar.css @media max-width:768px). Sin el escape por
      // `.closest('.admin-flyout')`, este `mousedown` la trataba como "fuera" y desmontaba el
      // flyout ANTES de que el navegador completara el `click` sobre el ítem elegido — el
      // `onClick` de `SectionGroup` nunca llegaba a correr (F-1/F-2, revisión de fix de Task 12).
      // El botón de cierre (`.admin-flyout-close`) vive dentro de `.admin-flyout` también, así que
      // este escape lo protege igual — pero sigue cerrando el flyout por SU PROPIO `onClick`, no
      // por este listener. `.mbn-admin-link` (el botón "Administración" del nav inferior móvil,
      // también fuera de `adminLinkRef`) necesita el mismo escape: sin él, un segundo clic sobre
      // ESE MISMO botón para cerrar el flyout (F-6) quedaba en la misma carrera — el `mousedown`
      // lo cerraba primero, y el `onClick` del botón (con `isAdminFlyoutOpen` de su closure aún en
      // `true`) lo volvía a abrir, dejando el segundo clic sin efecto neto.
      if (adminLinkRef.current?.contains(clickTarget)) return;
      if (clickTarget instanceof Element && clickTarget.closest('.admin-flyout, .mbn-admin-link')) return;
      setIsAdminFlyoutOpen(false);
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsAdminFlyoutOpen]);

  // Deep-link: llegar directo a `/admin`/`/admin/:section` (Pulso) o `/app/admin`/`/app/admin/:section`
  // (Reconocimiento) — bookmark, link compartido, redirect — debe abrir el flyout solo, igual que el
  // clic en el botón — sin esto el usuario ve el contenido de la sección pero ninguna navegación
  // para elegir otra (hueco encontrado en verificación manual de Task 12, reproducido en la zona
  // `/app/admin` en la revisión final — I-3: `isAdminPath` cubre ambas zonas, antes solo se
  // comparaba contra `VIEW_PATHS.admin`). Dependencia SOLO en `location.pathname`: el flyout cambia
  // de sección vía AdminPanelContext (`activeTab`), no vía navegación, así que elegir una sección
  // desde el flyout no cambia `location.pathname` y este efecto no debe volver a dispararse en ese
  // caso — de lo contrario reabriría el flyout después de que el usuario lo cierre deliberadamente
  // sin cambiar de URL. `setIsAdminFlyoutOpen` tiene identidad estable (useCallback en el Provider),
  // así que incluirlo en las deps no reintroduce ese problema.
  useEffect(() => {
    if (isAdminPath(location.pathname)) {
      setIsAdminFlyoutOpen(true);
    }
  }, [location.pathname, setIsAdminFlyoutOpen]);

  const inRecognitionZone = variant === 'recognition';

  // Prefer MSAL user when signed in; fall back to demo user profile. `getInitials` ya responde '?'
  // con nombre vacío (mismo criterio que UserConfigFAB) — sin ternario propio.
  const initials = currentUser?.initials ?? getInitials(demoUser.nombre);

  /**
   * RF-008 — el avatar elegido al darse de alta, resuelto id → url contra el catálogo cacheado.
   *
   * Precedencia (D4.b): si hay foto de Graph, gana la foto de Graph. Hoy `photoUrl` es siempre
   * `undefined`, así que en la práctica manda el avatar; la condición está escrita para el día que
   * Graph empiece a traer foto.
   *
   * Si no hay ninguna de las dos —o el id guardado ya no está en el contenedor— se queda las
   * iniciales de siempre: `useAvatarUrl` devuelve `undefined` cuando el id no está en el catálogo, así
   * que nunca sale una imagen rota.
   */
  const fotoDeGraph = currentUser?.photoUrl ?? demoUser.fotoUrl;
  const miPerfil = useUserProfile(demoUser.userId || null);
  const urlDelAvatar = useAvatarUrl(fotoDeGraph ? null : miPerfil.data?.avatar);
  const fotoDelSidebar = fotoDeGraph ?? urlDelAvatar;

  // Etiqueta del rol de mayor privilegio: es lo único para lo que se sigue usando primaryRole.
  // El mapa vive en auth/roleLabels — único puente entre los identificadores del backend y las
  // etiquetas visibles, para que ninguna pantalla rotule los roles a mano.
  const roleDisplayLabel = t(ROLE_LABEL_KEY[activeRole]);

  // Bloque "Reconocimientos": se gatea con el MISMO predicado que usa el menú del propio módulo
  // (RECOGNITION_ACCESS, solo por rol) — no con VIEW_MODULE. Ver docs/contexto/decisiones.md.
  //
  // Reconocer, Brillamos y la campaña personalizada además son mutuamente excluyentes: nunca hay
  // más de una campaña Activa por OU (el backend ya lo impide), así que el menú solo muestra la
  // entrada que corresponde a la campaña activa (o Reconocer, si no hay ninguna) — nunca las tres
  // juntas. `perfil` queda fuera del grupo excluyente: se sigue rigiendo solo por rol.
  // Mientras la campaña activa está en vuelo NO se muestra ninguna de las entradas excluyentes:
  // enseñar "Reconocer" por defecto y cambiarlo a "Brillamos" cuando resuelve la consulta produce
  // un salto visible en el menú. Es preferible no pintar la entrada hasta saber cuál corresponde.
  const { entryId: activeRecognitionEntryId, isLoading: recognitionEntryLoading } = useRecognitionEntryGate(demoUser.ou);
  const recognitionLinks = RECOGNITION_NAV_LINKS.filter(({ id }) => canAccessRecognitionEntry(id, activeRole)
    && (!EXCLUSIVE_RECOGNITION_NAV_IDS.includes(id)
      || (!recognitionEntryLoading && id === activeRecognitionEntryId)));

  // Mide en vivo cuánto espacio le queda al submenú de Administración — la fórmula cambia según el
  // modo (revisión de PR, hallazgo A-1): con el riel EXPANDIDO (inline) crece hacia ABAJO desde el
  // botón, acotado por el botón de colapsar + el pie (idioma/tema/perfil); con el riel COLAPSADO
  // (popover) crece hacia ARRIBA, porque el botón ahora vive cerca del FONDO del riel — el mismo
  // criterio que ya usa `.lang-menu-popover` para "Idioma" (ver Sidebar.css, override
  // `.side-nav--collapsed .admin-link-wrap--bottom .admin-flyout`). Recalcula al abrir/cerrar, al
  // colapsar/expandir el riel y al redimensionar la ventana (con `requestAnimationFrame` para no
  // forzar layout en cada evento de un drag de ventana); `visibleLinks.length`/
  // `recognitionLinks.length` cubren un cambio de rol (simulador de demo) que mueva la posición del
  // botón mientras el submenú ya está abierto. Se resetea a `null` al cerrar: sin esto la variable
  // CSS quedaba aplicada sobre un elemento que ya no la consume (inocuo, pero confunde al
  // inspeccionar el DOM).
  useLayoutEffect(() => {
    if (!isAdminFlyoutOpen) {
      setAdminFlyoutMaxHeight(null);
      setAdminFlyoutOpensDown(false);
      return undefined;
    }

    // Piso bajo (no 120+px): en una ventana muy baja, lo que ocupan los links de arriba + el botón
    // de colapsar + el pie (idioma/tema/perfil) puede por sí solo superar el alto de pantalla
    // disponible. Ante ese conflicto, la prioridad es la del pedido — que el pie del riel (o, en
    // popover, el borde superior de la ventana) se pueda ver — así que el propio submenú es lo que
    // cede primero.
    const MIN_FLYOUT_HEIGHT = 60;
    // Desglose real del espacio reservado en modo inline, entre el borde inferior medido del botón
    // y el borde inferior de la ventana (hallazgo M-1 de revisión de PR: el valor anterior, 24,
    // dejaba el pie del riel recortado ~13px porque faltaban dos sumandos):
    //   4px  margin-top de `.admin-flyout` inline
    //   5px  gap de `.side-nav-links`
    //   8px  margin-top de `.side-collapse-btn` (NO entra en su propio getBoundingClientRect().height)
    //   20px padding-bottom de `.side-nav`
    const RESERVED_GAP = 37;
    // Separación mínima respecto al borde SUPERIOR/INFERIOR de la ventana en modo popover: evita
    // que el flyout quede pegado al borde sin ningún respiro visual, crezca hacia el lado que
    // crezca.
    const TOP_BUFFER = 12;
    const BOTTOM_BUFFER = 12;
    let frame = 0;

    function recomputeMaxHeight() {
      if (collapsed) {
        const rect = adminButtonRef.current?.getBoundingClientRect();
        const spaceAbove = (rect?.top ?? 0) - TOP_BUFFER;
        const spaceBelow = window.innerHeight - (rect?.bottom ?? 0) - BOTTOM_BUFFER;
        // Empate a favor de "hacia arriba" (>=): es el criterio que ya tenían todos los roles con
        // el riel lleno, así que con espacio igual no cambia nada para ellos.
        const opensDown = spaceBelow > spaceAbove;
        setAdminFlyoutOpensDown(opensDown);
        setAdminFlyoutMaxHeight(Math.max(MIN_FLYOUT_HEIGHT, opensDown ? spaceBelow : spaceAbove));
        return;
      }

      const buttonBottom = adminButtonRef.current?.getBoundingClientRect().bottom ?? 0;
      const collapseBtnHeight = collapseBtnRef.current?.getBoundingClientRect().height ?? 0;
      const bottomBlockHeight = sideNavBottomRef.current?.getBoundingClientRect().height ?? 0;
      const available = window.innerHeight
        - buttonBottom - collapseBtnHeight - bottomBlockHeight - RESERVED_GAP;
      setAdminFlyoutMaxHeight(Math.max(MIN_FLYOUT_HEIGHT, available));
    }

    function handleResize() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(recomputeMaxHeight);
    }

    recomputeMaxHeight();
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, [isAdminFlyoutOpen, collapsed, visibleLinks.length, recognitionLinks.length]);

  // RF-074 — el nombre y el glifo que la campaña activa configuró para su entrada del menú. El hook
  // se llama SIEMPRE, aunque la entrada esté filtrada por rol: llamarlo condicionalmente rompería
  // la regla de los hooks en cuanto el rol cambiara sin recargar.
  const campaignEntry = useCampaignNavEntry(
    demoUser.ou,
    RECOGNITION_NAV_LINKS.find(({ id }) => id === CAMPAIGN_NAV_ID)?.emoji,
  );

  const collapseLabel = collapsed ? t('nav.expandMenu') : t('nav.collapseMenu');
  const themeLabel = theme === 'dark' ? t('common.lightMode') : t('common.darkMode');

  // Entrada "Administración": se renderiza aparte, al FONDO del riel (después del bloque de
  // Reconocimiento) — ver Sidebar.css `.admin-link-wrap--bottom`. El resto de `visibleLinks` se
  // recorre en su orden normal, saltándola.
  const adminLink = visibleLinks.find(({ view }) => view === 'admin');
  // `isAdminPath`, no igualdad exacta ni `startsWith(VIEW_PATHS.admin)` a mano: Task 11 (Step 7)
  // agregó la ruta hermana `/admin/:section` para los deep-links de Fundamentales — en
  // `/admin/campanas` la igualdad exacta perdía el estado activo y el primer clic navegaba de
  // vuelta a `/admin`, descartando el deep-link (fix de revisión de Task 11 — B-3). `isAdminPath`
  // además cubre `/app/admin*` (zona Reconocimiento), que `startsWith(VIEW_PATHS.admin)` nunca
  // hacía match (hallazgo I-3 de la revisión final: en `/app/admin/roles` este botón quedaba sin
  // estado activo y navegaba a `/admin` —zona de Pulso—, sacando al usuario de `/app` y perdiendo
  // el deep-link).
  const isOnAdmin = isAdminPath(location.pathname);

  return (
    <nav className={`side-nav${collapsed ? ' side-nav--collapsed' : ''}`}>
      <div className="side-nav-links">
        {/* Mismo bug que el "línea blanca" ya resuelto en AdminFlyout: sin `profileResolved`, las
            entradas del riel principal salen calculadas con el fallback de `useCurrentUser`
            (roles=[], sin permisos/módulos) hasta que el perfil real resuelve, unos segundos
            después de F5 — el usuario ve primero un menú genérico/reducido y luego el suyo real.
            Mientras tanto se muestra una carga explícita, igual que ya hace `AdminFlyout`. */}
        {!profileResolved ? (
          <PageSpinner minHeight="56px" />
        ) : (
          <>
            {visibleLinks.map(({ view, icon, labelKey }) => {
              if (view === 'admin') return null;

              return (
                <NavLink
                  key={view}
                  to={VIEW_PATHS[view]}
                  // "/" coincide con cualquier ruta por prefijo salvo que se pida coincidencia exacta.
                  end={VIEW_PATHS[view] === '/'}
                  className={({ isActive }) => `side-link${isActive ? ' active' : ''}`}
                  title={t(labelKey)}
                  aria-label={t(labelKey)}
                >
                  <i className={`fas ${icon}`} aria-hidden="true" />
                  <span className="side-link-label">{t(labelKey)}</span>
                </NavLink>
              );
            })}

            {/* Programa de Reconocimiento. Navegación SPA con `NavLink` en ambas zonas — la entrada
                activa se resalta también desde el portal, ya que comparte router con /app. */}
            {recognitionLinks.length > 0 && (
              <>
                <div className="side-nav-section-label">{t('nav.recognitionSection')}</div>
                <div className="reco-submenu">
                  {recognitionLinks.map(link => {
                    // RF-074 / WP-B2 — la entrada de la campaña personalizada toma su nombre y su glifo
                    // de lo que la campaña activa configuró en el paso «Diseño». Antes, `menuIcon` y
                    // `menuTitle` se escribían y no los leía NADIE: el menú enseñaba siempre el 🚀 y
                    // "Innovación" en duro, así que un administrador guardaba el nombre de su campaña sin
                    // error y no veía ningún cambio en ninguna parte.
                    //
                    // Solo afecta a esa entrada. Las demás —Reconocer, Brillamos, Perfil— son identidad
                    // de la plataforma y no las gobierna ninguna campaña (REQ-034).
                    const esCampaña = link.id === CAMPAIGN_NAV_ID;
                    const etiqueta = (esCampaña && campaignEntry.titulo) || tReco(link.labelKey);
                    const emoji = esCampaña ? campaignEntry.emoji : link.emoji;

                    return (
                      <NavLink
                        key={link.id}
                        to={link.to}
                        className={({ isActive }) => `reco-submenu-item${isActive ? ' reco-submenu-item--active' : ''}`}
                        title={etiqueta}
                        aria-label={etiqueta}
                      >
                        {emoji
                          ? <span className="reco-submenu-emoji" aria-hidden="true">{emoji}</span>
                          : <i className={recognitionIconClass(link)} aria-hidden="true" />}
                        <span>{etiqueta}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </>
            )}

            {/* Administración: relocada al fondo del riel, debajo de Reconocimiento (con margen de
                separación, ver `.admin-link-wrap--bottom`) — antes vivía intercalada entre
                "Fundamentals" y "Administradores". El chevron indica que expande inline dentro del
                propio sidebar (mismo fondo), no como popover aparte — salvo con el riel colapsado,
                donde conserva el popover flotante de siempre (ver Sidebar.css). */}
            {adminLink && (
              <div
                className={`admin-link-wrap admin-link-wrap--bottom${adminFlyoutOpensDown ? ' admin-link-wrap--flyout-down' : ''}`}
                ref={adminLinkRef}
                style={adminFlyoutMaxHeight != null
                  ? ({ '--admin-flyout-max-h': `${adminFlyoutMaxHeight}px` } as CSSProperties)
                  : undefined}
              >
                <button
                  ref={adminButtonRef}
                  type="button"
                  className={`side-link${isOnAdmin ? ' active' : ''}`}
                  title={t(adminLink.labelKey)}
                  aria-label={t(adminLink.labelKey)}
                  // Con el riel colapsado sigue siendo un popover flotante de verdad — ahí el atributo
                  // aporta semántica real. Expandido es un disclosure inline (acordeón dentro del
                  // propio riel): `aria-expanded` solo ya es correcto y suficiente.
                  aria-haspopup={collapsed ? 'true' : undefined}
                  aria-expanded={isAdminFlyoutOpen}
                  onClick={() => {
                    // Cada zona navega a SU PROPIA home de admin — nunca a la de la otra zona
                    // (`/admin` es de Pulso, `/app/admin` es de Reconocimiento). Sin distinguir por
                    // `inRecognitionZone`, este botón en `/app/admin/roles` navegaba a `/admin`
                    // (Pulso), sacando al usuario de `/app` y perdiendo la sección del deep-link.
                    if (!isOnAdmin) navigate(inRecognitionZone ? RECOGNITION_ADMIN_HOME : VIEW_PATHS.admin);
                    setIsAdminFlyoutOpen(!isAdminFlyoutOpen);
                  }}
                >
                  <i className={`fas ${adminLink.icon}`} aria-hidden="true" />
                  <span className="side-link-label">{t(adminLink.labelKey)}</span>
                  <i
                    className={`fas fa-chevron-${isAdminFlyoutOpen ? 'down' : 'right'} side-link-chevron`}
                    aria-hidden="true"
                  />
                </button>
                {isAdminFlyoutOpen && <AdminFlyout />}
              </div>
            )}
          </>
        )}

        <button
          ref={collapseBtnRef}
          type="button"
          className="side-collapse-btn"
          title={collapseLabel}
          aria-label={collapseLabel}
          aria-expanded={!collapsed}
          onClick={toggleCollapsed}
        >
          <i className="fas fa-chevron-left" aria-hidden="true" />
        </button>
      </div>

      <div className="side-nav-bottom" ref={sideNavBottomRef}>
        {/* El idioma vive siempre en el menú (Task 13 — unifica con la zona Reconocimiento, que
            antes lo mostraba en el GlobalLangDropdown flotante, ya retirado). La campana sigue
            siendo exclusiva de /app: depende del router de Reconocimiento, no tiene sentido en
            Pulso. Ver docs/contexto/decisiones.md. */}
        {inRecognitionZone && <NotificationBell />}
        <LanguageMenu />

        <button
          type="button"
          className="side-link side-theme-toggle"
          title={themeLabel}
          onClick={toggle}
          aria-label={themeLabel}
        >
          <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`} aria-hidden="true" />
          <span className="side-link-label">{themeLabel}</span>
        </button>

        <div className="side-nav-bottom-row">
          <SideProfileAvatar
            fotoUrl={fotoDelSidebar}
            seed={currentUser?.id || demoUser.userId || demoUser.nombre}
            initials={initials}
            ariaLabel={currentUser?.displayName ?? (demoUser.nombre || t('roles.employee'))}
          />
          <div className="side-role-label">{roleDisplayLabel}</div>
          <LogoutButton />
        </div>
      </div>
    </nav>
  );
}
